import { VisionButton } from "@/components/VisionUI"
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Slider } from "@/components/ui/slider"
import { Box, Flex, HStack, Text } from "@chakra-ui/react"
import { useCallback, useState } from "react"
import Cropper, { type Area } from "react-easy-crop"
import { useTranslation } from "react-i18next"
import { FiCrop, FiX, FiZoomIn, FiZoomOut } from "react-icons/fi"

interface LogoCropperProps {
  open: boolean
  imageSrc: string | null
  /** Cropper aspect ratio. Defaults to 2:1 (matches sidebar logo display). */
  aspect?: number
  /** Output image dimensions in px (longest side). Defaults to 800. */
  outputSize?: number
  onCancel: () => void
  onConfirm: (croppedBlob: Blob) => void
}

/**
 * Returns a cropped PNG blob of the given image, scaled so the longest output
 * side equals `outputSize`. The browser HTMLCanvas resizes the cropped region
 * with smoothing for clean downscaling — a UI-side defence-in-depth before the
 * backend does its own resize.
 */
async function getCroppedBlob(
  imageSrc: string,
  area: Area,
  outputSize: number,
  aspect: number,
): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = imageSrc
  })

  // Output width/height honoring the aspect, capped by outputSize on the long side.
  const outW = aspect >= 1 ? outputSize : Math.round(outputSize * aspect)
  const outH = aspect >= 1 ? Math.round(outputSize / aspect) : outputSize

  const canvas = document.createElement("canvas")
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Could not get 2D canvas context")
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = "high"
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    outW,
    outH,
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error("toBlob returned null"))
      else resolve(blob)
    }, "image/png")
  })
}

export function LogoCropper({
  open,
  imageSrc,
  aspect = 2,
  outputSize = 800,
  onCancel,
  onConfirm,
}: LogoCropperProps) {
  const { t } = useTranslation("admin")
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedArea, setCroppedArea] = useState<Area | null>(null)
  const [busy, setBusy] = useState(false)

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedArea(areaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!imageSrc || !croppedArea) return
    setBusy(true)
    try {
      const blob = await getCroppedBlob(
        imageSrc,
        croppedArea,
        outputSize,
        aspect,
      )
      onConfirm(blob)
    } finally {
      setBusy(false)
    }
  }

  const reset = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedArea(null)
    setBusy(false)
  }

  return (
    <DialogRoot
      open={open}
      onExitComplete={reset}
      size={{ base: "sm", md: "lg" }}
      placement="center"
    >
      <DialogBackdrop />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("branding.cropper.title")}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text fontSize="sm" color="gray.500" mb={3}>
            {t("branding.cropper.help")}
          </Text>
          <Box
            position="relative"
            w="100%"
            h="320px"
            bg="black"
            borderRadius="md"
            overflow="hidden"
          >
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid
                restrictPosition
              />
            )}
          </Box>
          <HStack mt={4} gap={3} align="center">
            <FiZoomOut aria-hidden />
            <Slider
              flex="1"
              min={1}
              max={3}
              step={0.05}
              value={[zoom]}
              onValueChange={(v) => setZoom(v.value[0] ?? 1)}
            />
            <FiZoomIn aria-hidden />
          </HStack>
        </DialogBody>
        <DialogFooter>
          <Flex gap={2} w="100%" justify="flex-end">
            <VisionButton variant="ghost" onClick={onCancel} disabled={busy}>
              <FiX />
              {t("branding.cancel")}
            </VisionButton>
            <VisionButton
              variant="brand"
              onClick={handleConfirm}
              loading={busy}
              disabled={!croppedArea}
            >
              <FiCrop />
              {t("branding.cropper.apply")}
            </VisionButton>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
